import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IonInput,
  IonTextarea,
  IonButton,
  IonSpinner,
  IonSkeletonText,
} from '@ionic/react';
import {
  personOutline,
  callOutline,
  locationOutline,
  documentTextOutline,
} from 'ionicons/icons';
import { ImpactStyle } from '@capacitor/haptics';
import ProSectionHeader from '../../components/pro/ProSectionHeader';
import ProCard from '../../components/pro/ProCard';
import {
  useProToolbarEnd,
  useProToolbarSticky,
} from '../../components/pro/ProToolbarSlot';
import { useProUnsavedLeaveGuard } from '../../components/pro/useProUnsavedLeaveGuard';
import api from '../../lib/api';
import { useCapacitorPlatform } from '@shared/hooks/useCapacitorPlatform';
import { useProToast, navStateWithToast } from './useProToast';
import './ProAccount.css';

const profileSchema = z
  .object({
    username: z.string().optional(),
    displayName: z.string().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    email: z.union([z.string().email('Invalid email'), z.literal('')]).optional(),
    dateOfBirth: z.string().optional(),
    gender: z.string().optional(),
    phone: z.string().optional(),
    address: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zipCode: z.string().optional(),
    occupation: z.string().optional(),
    education: z.string().optional(),
    politicalAffiliation: z.string().optional(),
    interests: z.string().optional(),
    bio: z.string().optional(),
  })
  // Mirror the backend invariant: `user_addresses.address_line_1` is
  // NOT NULL, so a row can only exist if there's a street line. Reject
  // submits that try to set city / state / zip without an address.
  .superRefine((data, ctx) => {
    const hasGeography =
      !!data.city?.trim() ||
      !!data.state?.trim() ||
      !!data.zipCode?.trim();
    const hasStreet = !!data.address?.trim();
    if (hasGeography && !hasStreet) {
      ctx.addIssue({
        path: ['address'],
        code: z.ZodIssueCode.custom,
        message: 'Street address is required when setting city, state, or ZIP.',
      });
    }
  });

type ProfileFormData = z.infer<typeof profileSchema>;

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS',
  'KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY',
  'NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV',
  'WI','WY','DC',
];

const EDUCATION_OPTIONS = [
  'No formal education',
  'Some high school',
  'High school diploma / GED',
  'Some college',
  'Associate degree',
  "Bachelor's degree",
  "Master's degree",
  'Professional degree',
  'Doctorate (PhD)',
  'Other',
];

const ProProfileEditPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showError } = useProToast();
  const { triggerHaptic } = useCapacitorPlatform();
  const [ready, setReady] = useState(false);

  const { data: profile, isLoading } = useQuery(
    ['pro-profile'],
    async () => {
      const { data } = await api.get('/users/profile');
      return data.data || data;
    }
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!profile || ready) return;
    reset({
      username: profile.username || '',
      displayName: profile.displayName || profile.display_name || '',
      firstName: profile.firstName || profile.first_name || '',
      lastName: profile.lastName || profile.last_name || '',
      email: profile.email || '',
      dateOfBirth: profile.dateOfBirth || profile.date_of_birth || '',
      gender: profile.gender || '',
      phone: profile.phone || '',
      address: profile.address || '',
      city: profile.city || '',
      state: profile.state || '',
      zipCode: profile.zipCode || profile.zip_code || '',
      occupation: profile.occupation || '',
      education: profile.education || '',
      politicalAffiliation:
        profile.politicalAffiliation || profile.political_affiliation || '',
      interests: profile.interests || '',
      bio: profile.bio || '',
    });
    setReady(true);
  }, [profile, reset, ready]);

  const saveMutation = useMutation(
    async (values: ProfileFormData) => {
      const { data } = await api.put('/users/profile', values);
      return data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['pro-profile']);
        triggerHaptic(ImpactStyle.Light);
        // Navigate immediately and hand the success toast to the profile
        // hub so the IonToast outlives this page's unmount. See
        // `usePendingToast` in ./useProToast.
        navigate('/pro/profile', {
          state: navStateWithToast({
            message: 'Profile updated.',
            color: 'success',
          }),
        });
      },
      onError: (err: any) => {
        showError(err?.response?.data?.error || 'Could not update profile.');
      },
    }
  );

  const onSubmit = (values: ProfileFormData) => {
    saveMutation.mutate(values);
  };

  // Pin the toolbar to the top for the lifetime of this page. Keeps the
  // Save button always reachable even when the form is scrolled.
  useProToolbarSticky(true);

  const unsavedLeaveSheet = useProUnsavedLeaveGuard({ isDirty });

  // When the user taps Save and validation fails, scroll the first visible
  // error message into view. We defer to the next animation frame so RHF
  // has updated `errors` and React has committed the error UI before we
  // query the DOM.
  const onInvalid = useCallback((_errors: Record<string, unknown>) => {
    requestAnimationFrame(() => {
      const firstError = document.querySelector<HTMLElement>(
        '.pro-account__field-error',
      );
      firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, []);

  // Render the Save button into the layout's toolbar right slot. Disabled
  // until the form is loaded, dirty, and not mid-submit. Hidden entirely
  // during loading so it never shows over the skeleton.
  useProToolbarEnd(
    ready && !isLoading ? (
      <IonButton
        onClick={handleSubmit(onSubmit, onInvalid)}
        disabled={!isDirty || saveMutation.isLoading}
        className="text-white font-semibold"
      >
        {saveMutation.isLoading ? (
          <IonSpinner name="crescent" className="text-white" />
        ) : (
          'Save'
        )}
      </IonButton>
    ) : null,
    [ready, isLoading, isDirty, saveMutation.isLoading, handleSubmit, onInvalid],
  );

  if (isLoading || !ready) {
    return (
      <div className="pro-account pb-8">
        <div className="px-2">
          <ProCard padded contentClassName="space-y-3">
            <IonSkeletonText animated style={{ width: '40%', height: '14px' }} />
            <IonSkeletonText animated style={{ width: '80%', height: '40px' }} />
            <IonSkeletonText animated style={{ width: '80%', height: '40px' }} />
            <IonSkeletonText animated style={{ width: '80%', height: '40px' }} />
          </ProCard>
        </div>
      </div>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="pro-account pb-8">
        <ProSectionHeader title="Identity" icon={personOutline} />
        <div className="px-2">
          <ProCard>
            <ProField label="Display name">
              <Controller
                control={control}
                name="displayName"
                render={({ field }) => (
                  <IonInput
                    className="pro-account__input"
                    fill="outline"
                    value={field.value || ''}
                    onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                    autocapitalize="words"
                  />
                )}
              />
            </ProField>
            <ProField label="First name">
              <Controller
                control={control}
                name="firstName"
                render={({ field }) => (
                  <IonInput
                    className="pro-account__input"
                    fill="outline"
                    value={field.value || ''}
                    onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                    autocapitalize="words"
                  />
                )}
              />
            </ProField>
            <ProField label="Last name">
              <Controller
                control={control}
                name="lastName"
                render={({ field }) => (
                  <IonInput
                    className="pro-account__input"
                    fill="outline"
                    value={field.value || ''}
                    onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                    autocapitalize="words"
                  />
                )}
              />
            </ProField>
            <ProField label="Username">
              <Controller
                control={control}
                name="username"
                render={({ field }) => (
                  <IonInput
                    className="pro-account__input"
                    fill="outline"
                    value={field.value || ''}
                    onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                    autocapitalize="none"
                  />
                )}
              />
            </ProField>
          </ProCard>
        </div>

        <ProSectionHeader title="Contact" icon={callOutline} />
        <div className="px-2">
          <ProCard>
            <ProField label="Email" error={errors.email?.message}>
              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <IonInput
                    className="pro-account__input"
                    fill="outline"
                    type="email"
                    inputmode="email"
                    value={field.value || ''}
                    onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                    autocapitalize="none"
                  />
                )}
              />
            </ProField>
            <ProField label="Phone">
              <Controller
                control={control}
                name="phone"
                render={({ field }) => (
                  <IonInput
                    className="pro-account__input"
                    fill="outline"
                    type="tel"
                    inputmode="tel"
                    value={field.value || ''}
                    onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                  />
                )}
              />
            </ProField>
          </ProCard>
        </div>

        <ProSectionHeader title="Address" icon={locationOutline} />
        <div className="px-2">
          <ProCard>
            <ProField label="Street" error={errors.address?.message}>
              <Controller
                control={control}
                name="address"
                render={({ field }) => (
                  <IonInput
                    className="pro-account__input"
                    fill="outline"
                    value={field.value || ''}
                    onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                    autocapitalize="words"
                  />
                )}
              />
            </ProField>
            <ProField label="City">
              <Controller
                control={control}
                name="city"
                render={({ field }) => (
                  <IonInput
                    className="pro-account__input"
                    fill="outline"
                    value={field.value || ''}
                    onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                    autocapitalize="words"
                  />
                )}
              />
            </ProField>
            <ProField label="State">
              <Controller
                control={control}
                name="state"
                render={({ field }) => (
                  <select
                    className="pro-account__select"
                    value={field.value || ''}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                    }}
                    onBlur={field.onBlur}
                  >
                    <option value="">—</option>
                    {US_STATES.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                )}
              />
            </ProField>
            <ProField label="ZIP code">
              <Controller
                control={control}
                name="zipCode"
                render={({ field }) => (
                  <IonInput
                    className="pro-account__input"
                    fill="outline"
                    inputmode="numeric"
                    value={field.value || ''}
                    onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                  />
                )}
              />
            </ProField>
          </ProCard>
        </div>

        <ProSectionHeader title="About" icon={documentTextOutline} />
        <div className="px-2">
          <ProCard>
            <ProField label="Date of birth">
              <Controller
                control={control}
                name="dateOfBirth"
                render={({ field }) => (
                  <IonInput
                    className="pro-account__input"
                    fill="outline"
                    type="date"
                    value={field.value || ''}
                    onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                  />
                )}
              />
            </ProField>
            <ProField label="Gender">
              <Controller
                control={control}
                name="gender"
                render={({ field }) => (
                  <select
                    className="pro-account__select"
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                  >
                    <option value="">—</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="non-binary">Non-binary</option>
                    <option value="prefer-not-to-say">Prefer not to say</option>
                  </select>
                )}
              />
            </ProField>
            <ProField label="Occupation">
              <Controller
                control={control}
                name="occupation"
                render={({ field }) => (
                  <IonInput
                    className="pro-account__input"
                    fill="outline"
                    value={field.value || ''}
                    onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                    autocapitalize="words"
                  />
                )}
              />
            </ProField>
            <ProField label="Education">
              <Controller
                control={control}
                name="education"
                render={({ field }) => (
                  <select
                    className="pro-account__select"
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                  >
                    <option value="">—</option>
                    {EDUCATION_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                )}
              />
            </ProField>
            <ProField label="Political affiliation">
              <Controller
                control={control}
                name="politicalAffiliation"
                render={({ field }) => (
                  <select
                    className="pro-account__select"
                    value={field.value || ''}
                    onChange={(e) => field.onChange(e.target.value)}
                    onBlur={field.onBlur}
                  >
                    <option value="">—</option>
                    <option value="democrat">Democrat</option>
                    <option value="republican">Republican</option>
                    <option value="independent">Independent</option>
                    <option value="libertarian">Libertarian</option>
                    <option value="green">Green</option>
                    <option value="other">Other</option>
                    <option value="prefer-not-to-say">Prefer not to say</option>
                  </select>
                )}
              />
            </ProField>
            <ProField label="Interests">
              <Controller
                control={control}
                name="interests"
                render={({ field }) => (
                  <IonInput
                    className="pro-account__input"
                    fill="outline"
                    placeholder="Comma separated"
                    value={field.value || ''}
                    onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                  />
                )}
              />
            </ProField>
            <ProField label="Bio">
              <Controller
                control={control}
                name="bio"
                render={({ field }) => (
                  <IonTextarea
                    className="pro-account__input"
                    fill="outline"
                    autoGrow
                    rows={3}
                    value={field.value || ''}
                    onIonInput={(e) => field.onChange(e.detail.value ?? '')}
                  />
                )}
              />
            </ProField>
          </ProCard>
        </div>
      </form>
      {unsavedLeaveSheet}
    </>
  );
};

interface ProFieldProps {
  label: string;
  error?: string;
  children: React.ReactNode;
}

const ProField: React.FC<ProFieldProps> = ({ label, error, children }) => (
  <div className="pro-account__field">
    <span className="pro-account__field-label">{label}</span>
    {children}
    {error && <p className="pro-account__field-error">{error}</p>}
  </div>
);

export default ProProfileEditPage;
